import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Box } from "@mui/material";

import { CardLayout, MTable } from "../v3";
import { getAssignedToolsTableColumns } from "../../pages/tools/constants";
import { ProjectService, ToolService } from "../../services";
import { derivePdfColumns, getPrintableRows, useHasPermission } from "../../utils";
import { MoveTool } from "./MoveTool.component";
import { EXPORT_TOOL, MOVE_TOOL, UPDATE_STOCK } from "../../constants";
import { UpdateQuantity } from ".";
import { handlePdfDownload, Listing } from "../pdf";
import { toast } from "react-toastify";

export const UsageHistory = ({ companyDetails = {}, toolDetails = [], setToolDetails = () => {}, reloadData = () => {} }) => {
  const [projects, setProjects] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [operationType, setOperationType] = useState("");
  const [selectedInventory, setSelectedInventory] = useState({});
  const [loading, setLoading] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);

  const { id } = useParams();
  const { user } = useSelector((state) => state.user);

  const hasMoveTool = useHasPermission(MOVE_TOOL);
  const hasUpdateStock = useHasPermission(UPDATE_STOCK);
  const hasToolExport = useHasPermission(EXPORT_TOOL);

  useEffect(() => {
    fetchData(id);
  }, [id]);

  const fetchData = async (toolId) => {
    setLoading(true);
    try {
      const { data: tool } = await ToolService.getToolById(toolId);
      const storeIds = tool?.toolInventory?.map((tool) => tool.storeId);
      if (storeIds?.length > 0) {
        const { data: projects } = await ProjectService.getProjectList({ storeIds: storeIds.join(","), limit: storeIds.length });
        setProjects(projects.rows);
      }
      setToolDetails(tool);
    } catch (error) {
      console.error("🚀 ~ fetchData ~ error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdfClick = async () => {
    setIsPdfGenerating(true);
    try {
      const pdfColumnHeaders = derivePdfColumns(getAssignedToolsTableColumns());
      const printableRows = getPrintableRows(rows);

      const pdfDocument = (
        <Listing
          title={`Usage History : ${toolDetails?.name}`}
          columnHeaders={pdfColumnHeaders}
          tableRows={printableRows}
          company={user?.company}
        />
      );

      await handlePdfDownload({
        document: pdfDocument,
        pdfTitle: `Usage History : ${toolDetails?.name}`,
      });
    } catch (error) {
      toast.error("PDF generation failed");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const handleOpenModal = (inventory, action) => {
    if (action === "move") setOpenModal(true);
    if (action === "increment" || action === "decrement") {
      setOperationType(action);
      setOpenUpdateModal(true);
    }
    setSelectedInventory(inventory);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setOpenUpdateModal(false);
    setSelectedInventory(null);
  };

  const handleActions = (data, actionType) => {
    switch (actionType) {
      case "move":
        handleOpenModal(data, actionType);
        break;

      case "increment":
        handleOpenModal(data, actionType);
        break;

      case "decrement":
        handleOpenModal(data, actionType);
        break;

      default:
        break;
    }
  };

  const columnHeaders = getAssignedToolsTableColumns(handleActions, hasMoveTool, hasUpdateStock);

  const rows = useMemo(() => {
    const inventories = toolDetails?.toolInventory || [];
    const companyInventory = inventories.find((inventory) => inventory?.storeId === companyDetails?.storeId);
    const projectInventories = inventories.filter(
      (inventory) => inventory?.storeId !== companyDetails?.storeId && inventory?.quantity !== 0,
    );
    const combinedInventories = [...(companyInventory ? [companyInventory] : []), ...projectInventories];

    return combinedInventories?.map((inventory, index) => {
      const project = projects.find((p) => p.storeId === inventory.storeId);
      return {
        sl: index + 1,
        id: inventory?.id,
        storeName: inventory?.storeId === companyDetails?.storeId ? `Company Warehouse [${companyDetails?.name}]` : project?.name,
        count: inventory?.quantity,
        storeId: inventory?.storeId,
      };
    });
  }, [toolDetails?.toolInventory, companyDetails?.storeId, companyDetails?.name, projects]);

  return (
    <>
      <CardLayout>
        <Box
          sx={{
            minHeight: { xs: "auto", md: "60vh" },
            maxHeight: "60vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <MTable
            // Export data
            showExportButton={hasToolExport}
            isPdfGenerating={isPdfGenerating}
            onExportPdfClick={handleDownloadPdfClick}
            // Table data
            columnHeaders={columnHeaders}
            tableRows={rows}
            loading={loading}
            hideFooter
            style={{ flex: 1, overflow: "auto" }}
          />
        </Box>
      </CardLayout>

      <MoveTool
        open={openModal}
        selectedInventory={selectedInventory}
        companyDetails={companyDetails}
        onClose={handleCloseModal}
        onSuccess={() => {
          fetchData(id);
          reloadData();
        }}
      />

      {/* update stock modal */}
      <UpdateQuantity
        open={openUpdateModal}
        operationType={operationType}
        selectedInventory={selectedInventory}
        onClose={handleCloseModal}
        onSuccess={() => {
          fetchData(id);
          reloadData();
        }}
      />
    </>
  );
};
